<template>
    <div class="user-form">
        <div v-if="dialogMode === 'add'" class="form-field">
            <label for="email">Email*</label>
            <InputText
                id="email"
                v-model.trim="formData.email"
                class="w-full"
                :class="{ 'p-invalid': !!emailError }"
                aria-describedby="email-error"
            />
            <small id="email-error" class="p-error" v-if="emailError">
                {{ emailError }}
            </small>
        </div>
        <div v-if="dialogMode === 'add'" class="form-field">
            <label for="name">Name</label>
            <InputText
                id="name"
                v-model.trim="formData.name"
                class="w-full"
                :class="{ 'p-invalid': submitted }"
                aria-describedby="name-error"
            />
        </div>
        <div class="form-field">
            <label for="role">Role</label>
            <Dropdown
                id="role"
                v-model="formData.role"
                :options="roles"
                optionLabel="label"
                optionValue="value"
                placeholder="Select a role"
                class="w-full"
                :class="{ 'p-invalid': !!roleError }"
                aria-describedby="role-error"
            />
            <small id="role-error" class="p-error" v-if="roleError">
                {{ roleError }}
            </small>
        </div>
        <div class="buttons">
            <Button
                label="Cancel"
                severity="secondary"
                @click="dialogRef?.close"
            />
            <Button
                :label="dialogMode === 'add' ? 'Add User' : 'Update Role'"
                severity="primary"
                @click="submit"
                :loading="isSubmitting"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { Ref, inject, ref, computed, onMounted } from "vue";
import { useToast } from "primevue/usetoast";
import { ClusterRole } from "@/stores/user-store";
import { useUsersStore } from "@/stores/users-store";
import type {
    UserDialogData,
    UserFormData,
} from "@/composables/user-management-dialog";
import type { DynamicDialogInstance } from "primevue/dynamicdialogoptions";
import Dropdown from "primevue/dropdown";

const dialogRef = inject<Ref<DynamicDialogInstance> | undefined>("dialogRef");

// Access dialog props and methods
const toast = useToast();
const usersStore = useUsersStore();

// Get data passed to the dialog
const dialogData = dialogRef!.value.data as UserDialogData;
const dialogMode = ref<"add" | "edit">(dialogData.mode);

// Form state
const submitted = ref(false);
const isSubmitting = ref(false);
const formData = ref<UserFormData>({
    email: "",
    role: ClusterRole.USER,
    userId: "",
});

// Email validation
const isValidEmail = computed(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return !formData.value.email || emailRegex.test(formData.value.email);
});

// Validation error messages
const emailError = computed(() => {
    if (!submitted.value) return "";
    if (!formData.value.email) return "Email address is required";
    if (!isValidEmail.value)
        return "Please enter a valid email address (e.g., user@company.com)";
    return "";
});

const roleError = computed(() => {
    if (!submitted.value) return "";
    if (!formData.value.role) return "Please select a role for this user";
    return "";
});

const roles = [
    { label: "User", value: "user" as ClusterRole },
    { label: "User Manager", value: "user-manager" as ClusterRole },
];

// Initialize the form
onMounted(() => {
    if (dialogMode.value === "edit" && dialogData.userData) {
        formData.value = {
            name: dialogData.userData.name,
            email: dialogData.userData.email,
            role: dialogData.userData.role as ClusterRole,
            userId: dialogData.userData.identity,
        };
    }
});

const submit = async () => {
    submitted.value = true;

    // Validate form
    if (emailError.value || roleError.value) {
        toast.add({
            severity: "warn",
            summary: "Please fix the validation errors",
            detail: "Check the form fields and correct any errors before submitting.",
            life: 4000,
        });
        return;
    }

    isSubmitting.value = true;
    try {
        if (dialogMode.value === "add") {
            await usersStore.addUserToOrganization(
                formData.value.email,
                formData.value.role,
                formData.value.name
            );
        } else {
            await usersStore.updateUserAuthorizationLevel(
                formData.value.userId,
                formData.value.role
            );
        }
        dialogRef?.value.close();
    } catch (error) {
        // The error is already handled by the store's handleError function
        // which shows appropriate toast messages, so we don't need to show another one here
        console.error("Error in user management dialog:", error);
    } finally {
        isSubmitting.value = false;
    }
};
</script>

<style scoped lang="scss">
.user-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 16px;
}

.form-field {
    display: flex;
    flex-direction: column;
    gap: 6px;

    label {
        font-weight: 500;
    }
}

.w-full {
    width: 100%;
}

.buttons {
    display: flex;
    justify-content: end;
    margin-top: 1em;
    gap: 1em;
}
</style>
